import { useEffect, useRef, useState } from 'react';
import type { Ticket } from '@vd/types';
import { UNTITLED_CAPTURE_PLACEHOLDER } from '@vd/types';

/**
 * Local-edit state machine for the ticket detail page's title +
 * description inputs.
 *
 * Coordinates four concerns that get tangled if you try to write them
 * inline:
 *
 * 1. **Server → local sync**: when the ticket query refetches (live
 *    event, AI title arrival), copy the server's title/description
 *    into the local input — UNLESS the user is actively editing that
 *    field. Without the dirty-flag gate, an AI-generated title
 *    arriving mid-typing clobbers the user's keystrokes.
 *
 * 2. **AI title-pending placeholder**: while the worker is queued to
 *    generate a title, the server stores the literal string
 *    `Untitled capture` and the client renders an empty input + an
 *    animated "Toado is thinking…" hint. The hook treats that
 *    placeholder as empty so the hint shows correctly.
 *
 * 3. **Description autosave**: 1500ms debounced save fires after the
 *    last keystroke. A blur handler at the call site fires too — the
 *    debounce is the safety net for users who don't blur.
 *
 * 4. **TicketId-change reset**: navigating between tickets must
 *    clear dirty flags AND cancel any pending autosave timer.
 *    Without this, ticket A's pending save fires AFTER ticketId has
 *    flipped, mutating B with A's text.
 *
 * 5. **beforeunload / visibility flush**: tab close mid-edit fires
 *    a final save (best-effort fire-and-forget — browsers can't
 *    await async work in unload).
 *
 * Pre-extraction this state machine lived in the body of
 * TicketDetail.tsx alongside page chrome, popovers, view-toggles,
 * keyboard shortcuts, the live subscriber, and the save-and-return
 * flow. Untestable. Now the rules above are one cohesive unit a
 * future contributor can read and modify in isolation.
 *
 * The hook does NOT own the actual network call — the caller supplies
 * `onAutosaveDescription` (typically a useMutation's mutate fn). That
 * keeps React Query's invalidation/error/loading concerns out of this
 * file, and makes it possible to reuse the hook in tests with a noop
 * save callback.
 */
export interface UseTicketDraftOptions {
  /** Called with the current description when the autosave timer fires. */
  onAutosaveDescription: (description: string) => void;
  /** Debounce ms; defaults to 1500. */
  autosaveDelayMs?: number;
}

export interface UseTicketDraftResult {
  title: string;
  description: string;
  setTitle: (v: string) => void;
  setDescription: (v: string) => void;
  /** True iff user has typed in the title field since the last save. */
  isTitleDirty: () => boolean;
  /** True iff user has typed in the description field since the last save. */
  isDescriptionDirty: () => boolean;
  /** Clear the dirty flag(s) for the fields that just successfully saved. */
  markSaved: (fields: { title?: boolean; description?: boolean }) => void;
  /** Cancel a pending debounced description save. */
  cancelDescriptionAutosave: () => void;
}

export function useTicketDraft(
  ticket: Pick<Ticket, 'id' | 'title' | 'description' | 'titlePending'> | undefined,
  options: UseTicketDraftOptions,
): UseTicketDraftResult {
  const { onAutosaveDescription, autosaveDelayMs = 1500 } = options;

  const [title, setTitleState] = useState('');
  const [description, setDescriptionState] = useState('');

  // Dirty flags as refs (not state) so a keystroke doesn't trigger a
  // re-render — only the input value setter does. Refs also let the
  // saveAndReturn-style flush call read them without subscribing.
  const titleDirtyRef = useRef(false);
  const descriptionDirtyRef = useRef(false);

  // Debounce timer for description autosave. Bumped on every keystroke.
  const descSaveTimerRef = useRef<number | null>(null);

  // Stable refs for the autosave callback + delay so the debounce
  // effect doesn't re-fire when the caller passes a new function
  // identity each render (which they almost always will).
  const onAutosaveDescriptionRef = useRef(onAutosaveDescription);
  onAutosaveDescriptionRef.current = onAutosaveDescription;

  const ticketId = ticket?.id;

  // Reset on ticketId change. Without this, ticket B inherits A's
  // dirty flags + a pending autosave timer that mutates B with A's
  // text after navigation.
  useEffect(() => {
    titleDirtyRef.current = false;
    descriptionDirtyRef.current = false;
    return () => {
      if (descSaveTimerRef.current !== null) {
        window.clearTimeout(descSaveTimerRef.current);
        descSaveTimerRef.current = null;
      }
    };
  }, [ticketId]);

  // Server → local sync, gated by dirty flags so live events don't
  // clobber in-progress typing.
  useEffect(() => {
    if (!ticket) return;
    if (!titleDirtyRef.current) {
      // Treat the AI-pending placeholder as empty so the input shows
      // the hint instead of the literal string.
      const isPendingPlaceholder = ticket.titlePending && ticket.title === UNTITLED_CAPTURE_PLACEHOLDER;
      setTitleState(isPendingPlaceholder ? '' : ticket.title);
    }
    if (!descriptionDirtyRef.current) {
      setDescriptionState(ticket.description);
    }
  }, [ticket]);

  // Description autosave debounce. Re-runs on every keystroke; the
  // cleanup clears the previous timer so only the latest one fires.
  useEffect(() => {
    if (!ticket) return;
    if (description === ticket.description) return;
    if (descSaveTimerRef.current) window.clearTimeout(descSaveTimerRef.current);
    descSaveTimerRef.current = window.setTimeout(() => {
      onAutosaveDescriptionRef.current(description);
    }, autosaveDelayMs);
    return () => {
      if (descSaveTimerRef.current) window.clearTimeout(descSaveTimerRef.current);
    };
  }, [description, ticket, autosaveDelayMs]);

  // beforeunload / visibility flush — best-effort save when the tab
  // closes mid-edit.
  useEffect(() => {
    const flush = () => {
      if (!ticket) return;
      if (description !== ticket.description) {
        onAutosaveDescriptionRef.current(description);
      }
    };
    const onVisChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, [description, ticket]);

  return {
    title,
    description,
    setTitle: (v: string) => {
      titleDirtyRef.current = true;
      setTitleState(v);
    },
    setDescription: (v: string) => {
      descriptionDirtyRef.current = true;
      setDescriptionState(v);
    },
    isTitleDirty: () => titleDirtyRef.current,
    isDescriptionDirty: () => descriptionDirtyRef.current,
    markSaved: (fields) => {
      if (fields.title !== undefined) titleDirtyRef.current = false;
      if (fields.description !== undefined) descriptionDirtyRef.current = false;
    },
    cancelDescriptionAutosave: () => {
      if (descSaveTimerRef.current !== null) {
        window.clearTimeout(descSaveTimerRef.current);
        descSaveTimerRef.current = null;
      }
    },
  };
}
